/**
 * Domain-aware routing helpers for Firebase multi-site hosting.
 * Landing and Back Office share one React build; hostname selects the surface.
 */

/**
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isBackOfficeHostname(hostname) {
  const host =
    typeof hostname === 'string'
      ? hostname
      : typeof window !== 'undefined'
        ? window.location.hostname
        : '';
  const normalized = String(host || '').toLowerCase();
  // "admin" covers admin.*, staging-admin.*, *-admin.web.app
  // startsWith("app.") only — not .includes("app"), which false-positives on *.web.app
  return normalized.includes('admin') || normalized.startsWith('app.');
}

/**
 * Absolute Sign In URL on the admin / app subdomain.
 * Prefer REACT_APP_ADMIN_ORIGIN when set (e.g. https://staging-admin.shiftypm.com).
 * @returns {string}
 */
export function getAdminSignInUrl() {
  const configured =
    typeof process.env.REACT_APP_ADMIN_ORIGIN === 'string'
      ? process.env.REACT_APP_ADMIN_ORIGIN.trim().replace(/\/$/, '')
      : '';

  if (configured) {
    return `${configured}/login`;
  }

  if (typeof window === 'undefined') {
    return '/login';
  }

  const { protocol, hostname, port } = window.location;
  const host = String(hostname || '').toLowerCase();

  if (host === 'localhost' || host === '127.0.0.1') {
    const origin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    return `${origin}/login`;
  }

  let adminHost;
  if (host.startsWith('staging.')) {
    adminHost = host.replace(/^staging\./, 'staging-admin.');
  } else if (host.startsWith('www.')) {
    adminHost = host.replace(/^www\./, 'admin.');
  } else {
    adminHost = `admin.${host}`;
  }

  return `${protocol}//${adminHost}/login`;
}

/**
 * Public origin for chat widget iframe src (landing host, not admin).
 * Prefer REACT_APP_PUBLIC_ORIGIN when set.
 * @returns {string}
 */
export function getPublicChatEmbedOrigin() {
  const configured =
    typeof process.env.REACT_APP_PUBLIC_ORIGIN === 'string'
      ? process.env.REACT_APP_PUBLIC_ORIGIN.trim().replace(/\/$/, '')
      : '';

  if (configured) {
    return configured;
  }

  if (typeof window === 'undefined') {
    return '';
  }

  const { protocol, hostname, port } = window.location;
  const host = String(hostname || '').toLowerCase();
  const portSuffix = port ? `:${port}` : '';

  if (host === 'localhost' || host === '127.0.0.1') {
    return `${protocol}//${hostname}${portSuffix}`;
  }

  if (host.includes('admin')) {
    if (host.endsWith('.web.app')) {
      const landingHost = host.replace(/-admin\.web\.app$/, '.web.app');
      return `${protocol}//${landingHost}`;
    }
    if (host.startsWith('staging-admin.')) {
      return `${protocol}//${host.replace(/^staging-admin\./, 'staging.')}`;
    }
    if (host.startsWith('admin.')) {
      return `${protocol}//${host.replace(/^admin\./, '')}`;
    }
  }

  return `${protocol}//${hostname}${portSuffix}`;
}
